import { ComponentPropsWithoutRef } from "react";
import { twMerge } from "tailwind-merge";

interface IInfoV2Props extends ComponentPropsWithoutRef<"div"> {
  /** Prefixes the content with a red "* " marker — for a note that's conditionally required reading, not for marking a required form field. */
  required?: boolean;
}

/**
 * The app's standard gray notice/info box (`bg-[#e8e8e8] rounded-[10px]`) — use it whenever
 * a page needs a callout below its main content (a caveat, a reminder, an instruction). It's
 * plain-text by default (`text-start md:text-center`) but takes arbitrary `children`, so it's
 * reusable as a wrapper for a richer layout too (e.g. an icon + title + description row) —
 * just override the alignment via `className` (pass `md:text-left`, not just `text-left`:
 * `twMerge` only dedupes classes within the same responsive-variant group, so a bare
 * `text-left` won't cancel this component's own `md:text-center` at the `md` breakpoint).
 */
export const InfoV2 = ({
  className,
  children,
  required,
  ...rest
}: IInfoV2Props) => {
  return (
    <div
      className={twMerge(
        "rounded-[10px] bg-[#e8e8e8] px-5 py-3 text-start md:text-center text-base text-[#20332f] w-full max-w-7xl mx-auto",
        className,
      )}
      {...rest}
    >
      {required && <span className="text-red-600">* </span>}
      {children}
    </div>
  );
};
